import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { useAppContext } from "../app/AppContext";
import { AuthErrorBox } from "./AuthGate";
import { AuthFlowError } from "./auth-errors";
import styles from "./VerificationBanner.module.css";

/**
 * App-wide, dismiss-resistant banner for unverified password accounts.
 * Verification is non-blocking by policy — the account keeps normal access —
 * so the banner only informs, resends, and re-checks. It disappears the
 * moment a re-exchange lands email_verified=true in the bootstrap.
 */
export function VerificationBanner() {
  const { viewer, resendVerification, refreshVerification } = useAppContext();
  const [busy, setBusy] = useState<"resend" | "check" | null>(null);
  const [feedback, setFeedback] = useState<"resent" | "still-unverified" | "no-session" | null>(
    null,
  );
  const [failure, setFailure] = useState<unknown>(null);

  if (!viewer || viewer.emailVerified) return null;

  function onFailure(error: unknown) {
    if (error instanceof AuthFlowError && error.code === "no_firebase_session") {
      setFeedback("no-session");
    } else {
      setFailure(error);
    }
  }

  function onResend() {
    setBusy("resend");
    setFeedback(null);
    setFailure(null);
    void resendVerification()
      .then(() => setFeedback("resent"))
      .catch(onFailure)
      .finally(() => setBusy(null));
  }

  function onCheck() {
    setBusy("check");
    setFeedback(null);
    setFailure(null);
    void refreshVerification()
      .then((verified) => {
        // On success the refreshed bootstrap unmounts the banner by itself.
        if (!verified) setFeedback("still-unverified");
      })
      .catch(onFailure)
      .finally(() => setBusy(null));
  }

  return (
    <div className={styles.banner} role="status">
      <p className={styles.copy}>
        <strong>
          <FormattedMessage id="auth.banner.title" />
        </strong>{" "}
        <FormattedMessage id="auth.banner.body" values={{ email: viewer.email ?? "" }} />
      </p>
      <div className={styles.actions}>
        <button type="button" onClick={onResend} disabled={busy !== null}>
          {busy === "resend" ? (
            <FormattedMessage id="auth.busy" />
          ) : (
            <FormattedMessage id="auth.banner.resend" />
          )}
        </button>
        <button type="button" onClick={onCheck} disabled={busy !== null}>
          {busy === "check" ? (
            <FormattedMessage id="auth.busy" />
          ) : (
            <FormattedMessage id="auth.banner.check" />
          )}
        </button>
      </div>
      {feedback ? (
        <p className={styles.feedback} role="status">
          <FormattedMessage
            id={
              feedback === "resent"
                ? "auth.banner.resent"
                : feedback === "still-unverified"
                  ? "auth.banner.stillUnverified"
                  : "auth.banner.noSession"
            }
          />
        </p>
      ) : null}
      <AuthErrorBox error={failure} />
    </div>
  );
}
